import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Edit2, Check, Palette } from 'lucide-react';
import { supabase } from '../supabase/client';

interface Tag {
  id: string;
  name: string;
  color: string;
  sort_order: number;
}

interface TagManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981',
  '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#d946ef',
  '#f43f5e', '#78716c', '#374151'
];

export default function TagManagerModal({ isOpen, onClose }: TagManagerModalProps) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (isOpen) {
      loadTags();
    }
  }, [isOpen]);

  const loadTags = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('teacher_student_tags')
      .select('*')
      .eq('teacher_id', user.id)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) {
      console.error('加载标签失败:', error);
    } else {
      setTags(data || []);
    }
    setLoading(false);
  };

  const handleAddTag = async () => {
    if (!newTagName.trim()) {
      setMessage({ type: 'error', text: '请输入标签名称' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('teacher_student_tags')
      .insert({
        teacher_id: user.id,
        name: newTagName.trim(),
        color: newTagColor,
        sort_order: tags.length
      })
      .select();

    if (error) {
      setMessage({ type: 'error', text: '添加标签失败' });
    } else {
      setMessage({ type: 'success', text: '标签添加成功' });
      setNewTagName('');
      setNewTagColor('#6366f1');
      setTags([...tags, data[0]]);
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleDeleteTag = async (tagId: string) => {
    const { error } = await supabase
      .from('teacher_student_tags')
      .delete()
      .eq('id', tagId);

    if (error) {
      setMessage({ type: 'error', text: '删除标签失败' });
    } else {
      setMessage({ type: 'success', text: '标签已删除' });
      setTags(tags.filter(t => t.id !== tagId));
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleStartEdit = (tag: Tag) => {
    setEditingTag(tag);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  const handleSaveEdit = async () => {
    if (!editingTag || !editName.trim()) return;

    const { error } = await supabase
      .from('teacher_student_tags')
      .update({ name: editName.trim(), color: editColor })
      .eq('id', editingTag.id);

    if (error) {
      setMessage({ type: 'error', text: '更新标签失败' });
    } else {
      setMessage({ type: 'success', text: '标签已更新' });
      setTags(tags.map(t => t.id === editingTag.id ? { ...t, name: editName.trim(), color: editColor } : t));
      setEditingTag(null);
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleMoveTag = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === tags.length - 1) return;

    const newTags = [...tags];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newTags[index], newTags[targetIndex]] = [newTags[targetIndex], newTags[index]];

    // Update sort_order for all tags
    const updates = newTags.map((tag, i) => ({
      id: tag.id,
      sort_order: i
    }));

    for (const update of updates) {
      await supabase
        .from('teacher_student_tags')
        .update({ sort_order: update.sort_order })
        .eq('id', update.id);
    }

    setTags(newTags);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            className="bg-white rounded-xl p-6 w-[500px] max-h-[80vh] overflow-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">标签管理</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {message.text && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-3 rounded-lg mb-4 ${
                  message.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                }`}
              >
                {message.text}
              </motion.div>
            )}

            {/* Add new tag */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">添加新标签</h4>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="输入标签名称"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                />
                <button
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  添加
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-gray-400" />
                <div className="flex gap-2 flex-wrap">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => setNewTagColor(color)}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${
                        newTagColor === color ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Tag list */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">已有标签</h4>
              {loading ? (
                <div className="text-center py-8 text-gray-400">加载中...</div>
              ) : tags.length === 0 ? (
                <div className="text-center py-8 text-gray-400">暂无标签</div>
              ) : (
                <div className="space-y-2">
                  {tags.map((tag, index) => (
                    <div
                      key={tag.id}
                      className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                    >
                      {editingTag?.id === tag.id ? (
                        <>
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                            />
                            <div className="flex gap-1">
                              {PRESET_COLORS.slice(0, 6).map(color => (
                                <button
                                  key={color}
                                  onClick={() => setEditColor(color)}
                                  className={`w-5 h-5 rounded-full border-2 ${
                                    editColor === color ? 'border-gray-800' : 'border-transparent'
                                  }`}
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </div>
                          </div>
                          <button
                            onClick={handleSaveEdit}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingTag(null)}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 flex-1">
                            <span
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: tag.color }}
                            >
                              {tag.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleMoveTag(index, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:bg-gray-100 rounded disabled:opacity-30"
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => handleMoveTag(index, 'down')}
                              disabled={index === tags.length - 1}
                              className="p-1 text-gray-400 hover:bg-gray-100 rounded disabled:opacity-30"
                            >
                              ↓
                            </button>
                            <button
                              onClick={() => handleStartEdit(tag)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTag(tag.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
              >
                关闭
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
